import { Stack, useLocalSearchParams } from "expo-router"
import {
  type LoanApplication,
  LoanContext,
  NewLoanContext,
  type PlaceData,
} from "@/screens/loans/components/LoanContext"
import { type LoanApplicationInput } from "@/generated/graphql"
import { useCallback, useState } from "react"
import { useStore } from "@nanostores/react"
import {
  addLoanUpdates,
  getLoanUpdateStore,
  pushToServer,
  setLoanUpdates,
} from "@/stores/loanState"
import { hasRewards } from "@/utils/UserUtils"
import { useUser } from "@/queries/user"
import { useAddLoan, useCombinedLoanData, useEditLoan } from "@/queries/loan"
import { showErrorToast } from "@/components/toasts/AppToast"
import { loanAppErrorEncountered } from "@/analytics/events/loans"
import type { ClientError } from "graphql-request"
import { Sentry } from "@/hooks/sentry"

export const NEW_LOAN_ID = "__new"

export default () => {
  const { _loanId } = useLocalSearchParams<{ _loanId?: string }>()
  const [loanKey] = useState(_loanId || NEW_LOAN_ID)
  const [loanId, setLoanId] = useState<string | undefined>(_loanId)
  const [place, setPlaceData] = useState<PlaceData | undefined>(undefined)
  const [placeLoadError, setPlaceLoadError] = useState<string | undefined>(
    undefined
  )

  const loanUpdateState = useStore(getLoanUpdateStore(loanKey))
  const updates = loanUpdateState.updates

  const { data: userData } = useUser()
  const { id: userId, firstName, lastName } = userData?.me || {}

  const {
    data: loan,
    // error,
    refetch: refetchLoanData,
  } = useCombinedLoanData(loanId)

  const { mutate: updateLoanMutation } = useEditLoan()
  const addLoanMutation = useAddLoan()

  const update = (u: LoanApplicationInput) => {
    addLoanUpdates(loanKey, u)
  }

  // Compute userHasRewards based on userData
  const userHasRewards = hasRewards(userData?.me || {})

  const loanApplication = {
    ...loan?.loanApplication,
    ...loanUpdateState.sentToServer.reduce<LoanApplicationInput>(
      (acc, obj) => ({
        ...acc,
        ...obj,
      }),
      {}
    ),
    ...updates,
  }

  const clear = useCallback(() => {
    setLoanUpdates(loanKey, {})
  }, [loanId])

  const doSave = async (newLoanId: string) => {
    await new Promise<void>((resolve, reject) => {
      const updatesToServer = pushToServer(loanKey) // updateRef.current
      console.log(`loan(${newLoanId}): save(${JSON.stringify(updatesToServer)}`)

      updateLoanMutation(
        {
          loanId: newLoanId,
          loanInput: updatesToServer,
        },
        {
          onSuccess: (data) => {
            console.log(
              `loan(${newLoanId}): save.onSuccess(${JSON.stringify(data)}`
            )
            resolve()
          },
          onError: (err) => {
            addLoanUpdates(newLoanId, updatesToServer)
            const displayMessage =
              "Please give us a call at 844.363.5477 to continue."
            showErrorToast({
              title: "Loan Processing Error",
              message: displayMessage,
            })
            loanAppErrorEncountered({
              error: err as ClientError,
              displayedMessage: displayMessage,
              propertyAddress:
                loan?.loanApplication?.property.fullAddress || "Unknown",
              userId: String(userId),
              userName: `${firstName} ${lastName}`,
            })
            reject(err)
          },
        }
      )
    })
  }

  const save = async () => {
    if (!loanId) {
      console.error("No loanID yet, skipping save()")
      return
    }
    await doSave(loanId)
  }

  function setPlace(place: PlaceData) {
    setPlaceData(place)
    setPlaceLoadError(undefined)
    if (!place) return

    addLoanMutation.mutate(
      { placeId: place.placeId },
      {
        onSuccess: (data) => {
          const loanApplication = data?.createLoanApplication?.loanApplication
          if (!loanApplication) {
            setPlaceLoadError("Problem finding property, please try again")
            return
          }
          void doSave(loanApplication.id).then(() => {
            setLoanId(loanApplication?.id || "")
          })
        },
        onError: (err) => {
          setPlaceLoadError(err.message)
          Sentry.captureException(err)
        },
      }
    )
  }

  return (
    <NewLoanContext.Provider
      key={"NewLoanContext"}
      value={{ loanId, setLoanId, setPlace, place, placeLoadError }}
    >
      <LoanContext.Provider
        key={"loanContext"}
        value={{
          loanApplication: loanApplication as LoanApplication,
          capitalProducts: loan?.capitalProducts || [],
          loanId: loanId || "",
          update,
          save,
          clear,
          refetch: async () => {
            await refetchLoanData()
          },
          userHasRewards,
          isLoading: false,
          isError: false,
          cashToCloseDisplaysAsRange:
            !userHasRewards && loanApplication?.loanProduct !== "refi",
        }}
      >
        <Stack screenOptions={{ headerShown: false, animation: "fade" }} />
      </LoanContext.Provider>
    </NewLoanContext.Provider>
  )
}
